import { createApi } from '@reduxjs/toolkit/query/react';

import {
  deleteConsentSubject,
  fetchActiveConsentPolicy,
  fetchConsentHistory,
  fetchCurrentConsent,
  recordConsent,
  withdrawConsent,
} from '../util/api';

/**
 * Wrap any imperative `api.js` helper as an RTK Query `queryFn` so the
 * RTK cache layers on top of the canonical fetcher.
 *
 * @template A,R
 * @param {(arg: A) => Promise<R>} fn
 * @returns {(arg: A) => Promise<{data: R} | {error: any}>}
 */
const wrap = fn => async arg => {
  try {
    return { data: await fn(arg) };
  } catch (e) {
    return { error: { status: e.status || 500, data: { message: e.message } } };
  }
};

/** RTK Query layer over the cookie-consent BFF endpoints in `src/util/api.js`. */
export const consentApi = createApi({
  reducerPath: 'consentApi',
  baseQuery: () => ({ data: null }),
  tagTypes: ['ActivePolicy', 'CurrentConsent', 'ConsentHistory'],
  endpoints: build => ({
    /** Active policy version + Sharetribe asset version pin. */
    fetchActiveConsentPolicy: build.query({
      queryFn: wrap(fetchActiveConsentPolicy),
      providesTags: ['ActivePolicy'],
    }),
    /** Caller's current decision (or `null` when none recorded). */
    fetchCurrentConsent: build.query({
      queryFn: wrap(fetchCurrentConsent),
      providesTags: ['CurrentConsent'],
    }),
    /** DSAR self-export. Logged-in users only. */
    fetchConsentHistory: build.query({
      queryFn: wrap(fetchConsentHistory),
      providesTags: ['ConsentHistory'],
    }),
    /** Append a consent decision; invalidates current + history. */
    recordConsent: build.mutation({
      queryFn: wrap(recordConsent),
      invalidatesTags: ['CurrentConsent', 'ConsentHistory'],
    }),
    /** Record a `withdraw`; invalidates current + history. */
    withdrawConsent: build.mutation({
      queryFn: wrap(withdrawConsent),
      invalidatesTags: ['CurrentConsent', 'ConsentHistory'],
    }),
    /** Right-to-erasure; invalidates everything consent-shaped. */
    deleteConsentSubject: build.mutation({
      queryFn: wrap(deleteConsentSubject),
      invalidatesTags: ['ActivePolicy', 'CurrentConsent', 'ConsentHistory'],
    }),
  }),
});

export const {
  useFetchActiveConsentPolicyQuery,
  useFetchCurrentConsentQuery,
  useFetchConsentHistoryQuery,
  useRecordConsentMutation,
  useWithdrawConsentMutation,
  useDeleteConsentSubjectMutation,
} = consentApi;

export default consentApi.reducer;
